import { Injectable, Output, EventEmitter, NgZone } from '@angular/core';
import { DataService } from 'src/app/service/data.service';

export type WorkerMessage =
	| { type: 'downloadProgress'; loaded: number; total: number; startTime: number }
	| { type: 'downloadDone'; blob: Blob, performance: any }
	| { type: 'downloadError'; error: string }
	| { type: 'uploadProgress'; loaded: number; total: number; startTime: number }
	| { type: 'uploadDone' }
	| { type: 'uploadError'; error: string };

@Injectable()
export class SpeedTestService {
	private upload: EventEmitter<any> = new EventEmitter();
	private download: EventEmitter<any> = new EventEmitter();
	public reqs: any[] = [];
	public chartInstance: any[] = [];
	private worker: Worker | null = null;
	private workerReady: Promise<void> | null = null;
	private currentDownloadResolve: ((item: any) => void) | null = null;
	private currentDownloadReject: ((err: any) => void) | null = null;
	private currentUploadResolve: ((item: any) => void) | null = null;
	private currentUploadReject: ((err: any) => void) | null = null;
	private currentItem: any = null;

	constructor(private datas: DataService, private zone: NgZone) {}

	testRunning(updown: string, res: ProgressEvent, item: any): void {
		if (!item[updown + 'Started']) return;
		const now = Date.now();
		let total = res.lengthComputable ? res.total : 0;
		if (updown === 'upload') {
			total = item.binaryData ? item.binaryData.size : total;
		}
		if (res.lengthComputable && total && item[updown + 'Status'] == null) {
			const diff = (now - item.start) / 1000;
			const Bps = res.loaded / diff;
			const mbit = (Bps / 1024 / 1024) * 8;
			item[updown] = Math.round(mbit * 10) / 10;
			item[updown] = item[updown] > 100 ? Math.round(item[updown]) : item[updown];
			item[updown + 'Queue'] = [...(item[updown + 'Queue'] || []), mbit];
			item[updown + 'Queue'] = item[updown + 'Queue'].filter((_: any, i: number) => i < 5);
			if (item[updown + 'Chart']) {
				item[updown + 'Chart'].data.datasets[0].data = item[updown + 'Queue'];
				try {
					item[updown + 'Chart'].update();
				} catch (e) {}
			}
		}
	}

	testDownload(item: any): Promise<any> {
		const w = this.getWorker();
		if (w) {
			return new Promise((resolve, reject) => {
				item.start = Date.now();
				item.testStatus = 'downloading';
				this.currentItem = item;
				this.currentDownloadResolve = resolve;
				this.currentDownloadReject = reject;
				const url = this.getAbsoluteUrl(item.downloadUrl);
				w.postMessage({ type: 'download', url });
			});
		}
		return this.testDownloadMainThread(item);
	}

	private testDownloadMainThread(item: any): Promise<any> {
		return new Promise((resolve, reject) => {
			item.req = new XMLHttpRequest();
			this.reqs.push(item.req);
			item.start = Date.now();
			item.testStatus = 'downloading';

			item.req.onprogress = (res: ProgressEvent) => {
				this.zone.run(() => {
					if (item.downloadStarted && item.downloadStatus == null) {
						this.testRunning('download', res, item);
					}
				});
			};

			item.req.onreadystatechange = () => {
				this.zone.run(() => {
					if (item.req && item.req.readyState === 4 && item.downloadStarted) {
						item.binaryData = item.req.response;
						resolve(item);
					}
				});
			};
			item.req.onerror = () => {
				this.zone.run(() => {
					if (item.downloadStarted) reject(item.req?.statusText || 'error');
				});
			};
			const downloadUrl =
				item.downloadUrl + (item.downloadUrl.indexOf('?') > -1 ? '&ts=' : '?ts=') + Date.now();
			item.req.open('GET', downloadUrl, true);
			item.req.responseType = 'blob';
			item.req.send(null);
			setTimeout(() => {
				this.zone.run(() => {
					if (item.req && item.downloadStarted) {
						item.req.abort();
					}
					reject('timeout');
				});
			}, 20000);
		});
	}

	testUpload(item: any): Promise<any> {
		const w = this.getWorker();
		if (w && item.binaryData) {
			return new Promise((resolve, reject) => {
				item.start = Date.now();
				item.testStatus = 'uploading';
				this.currentItem = item;
				this.currentUploadResolve = resolve;
				this.currentUploadReject = reject;
				const url = this.getAbsoluteUrl(item.uploadUrl);
				w.postMessage({ type: 'upload', url, blob: item.binaryData });
			});
		}
		return this.testUploadMainThread(item);
	}

	private testUploadMainThread(item: any): Promise<any> {
		return new Promise((resolve, reject) => {
			if (item.req) {
				item.req.abort();
			}
			if (!item.uploadStarted) {
				reject('upload not started');
				return;
			}
			item.req = new XMLHttpRequest();
			this.reqs.push(item.req);
			item.start = Date.now();
			item.testStatus = 'uploading';
			if (item.req.upload) {
				item.req.upload.onprogress = (res: ProgressEvent) => {
					if (item.uploadStatus == null && item.uploadStarted) {
						this.testRunning('upload', res, item);
					}
				};
				item.req.upload.onloadend = () => resolve(item);
			} else {
				item.req.onprogress = (res: ProgressEvent) => {
					if (item.uploadStatus == null && item.uploadStarted) {
						this.testRunning('upload', res, item);
					}
				};
				item.req.onreadystatechange = () => {
					if (item.req && item.req.readyState === 4 && item.uploadStarted) {
						resolve(item);
					}
				};
			}
			item.req.onerror = () => {
				if (item.uploadStarted) reject(item.req?.statusText || 'error');
			};
			const uploadUrl =
				item.uploadUrl + (item.uploadUrl.indexOf('?') > -1 ? '&ts=' : '?ts=') + Date.now();
			item.req.open('POST', uploadUrl, true);
			item.req.send(item.binaryData);
			setTimeout(() => {
				this.zone.run(() => {
					if (item.req && item.uploadStarted) {
						item.req.abort();
						reject('timeout');
					}
				});
			}, 20000);
		});
	}

	cancelAllReq(): void {
		if (this.worker) {
			this.worker.postMessage({ type: 'cancel' });
		}
		if (this.currentDownloadReject) {
			this.currentDownloadReject('cancelled');
			this.currentDownloadResolve = this.currentDownloadReject = null;
		}
		if (this.currentUploadReject) {
			this.currentUploadReject('cancelled');
			this.currentUploadResolve = this.currentUploadReject = null;
		}
		this.currentItem = null;
		this.reqs.forEach((r) => {
			try {
				r.abort();
			} catch (e) {}
		});
		this.reqs = [];
	}
	private getWorker(): Worker | null {
		if (typeof Worker === 'undefined') {
			return null;
		}
		if (this.worker) {
			return this.worker;
		}
		try {
			this.worker = new Worker('./speedtest.worker',  { type: 'module' });
			this.worker.onmessage = (e: MessageEvent<WorkerMessage>) => this.zone.run(() => this.handleWorkerMessage(e.data));
			this.worker.onerror = (err) => {
				this.zone.run(() => {
					if (this.currentDownloadReject) {
						this.currentDownloadReject(err);
						this.currentDownloadResolve = this.currentDownloadReject = null;
					}
					if (this.currentUploadReject) {
						this.currentUploadReject(err);
						this.currentUploadResolve = this.currentUploadReject = null;
					}
					this.currentItem = null;
				});
			};
			return this.worker;
		} catch {
			return null;
		}
	}

	private getAbsoluteUrl(url: string): string {
		if (typeof window === 'undefined' || !url) return url;
		if (url.startsWith('//')) return window.location.protocol + url;
		return url;
	}

	private handleWorkerMessage(data: WorkerMessage): void {
		const item = this.currentItem;
		if (!item) return;

		switch (data.type) {
			case 'downloadProgress':
				if (item.downloadStarted && item.downloadStatus == null) {
					this.testRunningFromWorker('download', data, item);
				}
				break;
			case 'downloadDone':
				item.binaryData = data.blob;
				if (data.performance) {
					item.performance = data.performance;
					if (item.performance.speed !== undefined) {
						item.download = item.performance.speed;
					}
				}
				console.log('Download done, blob size:', item.binaryData.size);
				if (this.currentDownloadResolve) {
					this.currentDownloadResolve(item);
					this.currentDownloadResolve = this.currentDownloadReject = null;
				}
				this.currentItem = null;
				break;
			case 'downloadError':
				if (this.currentDownloadReject) {
					this.currentDownloadReject(data.error);
					this.currentDownloadResolve = this.currentDownloadReject = null;
				}
				this.currentItem = null;
				break;
			case 'uploadProgress':
				if (item.uploadStarted && item.uploadStatus == null) {
					this.testRunningFromWorker('upload', data, item);
				}
				break;
			case 'uploadDone':
				if (this.currentUploadResolve) {
					this.currentUploadResolve(item);
					this.currentUploadResolve = this.currentUploadReject = null;
				}
				this.currentItem = null;
				break;
			case 'uploadError':
				if (this.currentUploadReject) {
					this.currentUploadReject(data.error);
					this.currentUploadResolve = this.currentUploadReject = null;
				}
				this.currentItem = null;
				break;
		}
	}

	private testRunningFromWorker(
		updown: 'download' | 'upload',
		data: { loaded: number; total: number; startTime: number },
		item: any
	): void {
		if (!item[updown + 'Started'] || item[updown + 'Status'] != null) return;
		const { loaded, total, startTime } = data;
		if (!total) return;
		const diff = (Date.now() - startTime) / 1000;
		if (diff <= 0) return;
		const Bps = loaded / diff;
		const mbit = (Bps / 1024 / 1024) * 8;
		item[updown] = Math.round(mbit * 10) / 10;
		item[updown] = item[updown] > 100 ? Math.round(item[updown]) : item[updown];
		item[updown + 'Queue'] = [...(item[updown + 'Queue'] || []), mbit];
		item[updown + 'Queue'] = item[updown + 'Queue'].filter((_: any, i: number) => i < 5);
		if (item[updown + 'Chart']) {
			item[updown + 'Chart'].data.datasets[0].data = item[updown + 'Queue'];
			try {
				item[updown + 'Chart'].update();
			} catch (e) {}
		}
	}
}
